/**
 * Original exam pictures (SVG drawings) of the Academy, shared by the web and the mobile app:
 * the picture of a question (TCF CO « Regardez l'image »), the drawings of the options (TEF CO
 * section 1, Goethe A2 Hören Teil 3), the plan of an IELTS part and the pool of a Goethe match.
 * The server sends short-lived signed links: the web shows the SVG (`url`), React Native the PNG
 * copy (`png_url`). Links are never kept across sittings. No React and no platform API here.
 */

import { isObject, num, str } from "../ielts/read";

export interface PictureAlt {
  fr: string;
  en: string;
}

export interface ExamPicture {
  key: string;
  /** Signed link to the SVG drawing (web). */
  url: string;
  /** Signed link to a PNG copy (React Native cannot draw SVG), or null. */
  png_url: string | null;
  alt: PictureAlt;
  width: number;
  height: number;
}

export type PicturePlatform = "web" | "native";

/** A picture sent by the server, read defensively (null when nothing usable is there). */
export function parsePicture(value: unknown): ExamPicture | null {
  if (!isObject(value)) return null;
  const rawAlt = value.alt;
  const alt: PictureAlt = isObject(rawAlt)
    ? { fr: str(rawAlt.fr), en: str(rawAlt.en) }
    : { fr: str(rawAlt), en: str(rawAlt) };
  const url = str(value.url);
  const png = str(value.png_url);
  if (!url && !png && !alt.fr && !alt.en) return null;
  return {
    key: str(value.key),
    url,
    png_url: png || null,
    alt,
    width: Math.max(0, num(value.width)),
    height: Math.max(0, num(value.height)),
  };
}

/** The pictures of the options (one per option, null where an option has none). */
export function parsePictures(value: unknown): (ExamPicture | null)[] {
  return Array.isArray(value) ? value.map(parsePicture) : [];
}

/** At least one option is a picture: the options are shown as picture cards. */
export function hasPictures(list: readonly (ExamPicture | null)[] | null | undefined): boolean {
  return Boolean(list?.some((p) => p !== null));
}

/** The text alternative in the interface language (French, English, then whichever exists). */
export function pictureAlt(picture: ExamPicture | null | undefined, language: string): string {
  if (!picture) return "";
  const english = language.toLowerCase().startsWith("en");
  return (english ? picture.alt.en || picture.alt.fr : picture.alt.fr || picture.alt.en) || "";
}

/** The link to show: the SVG on the web, the PNG copy on the phone (null: show the alt text). */
export function pictureSource(picture: ExamPicture | null | undefined, platform: PicturePlatform): string | null {
  if (!picture) return null;
  if (platform === "native") return picture.png_url || null;
  return picture.url || null;
}

/** Width / height of the drawing (4:3 when the server does not say), to avoid layout shifts. */
export function pictureRatio(picture: ExamPicture | null | undefined): number {
  if (!picture || picture.width <= 0 || picture.height <= 0) return 4 / 3;
  return picture.width / picture.height;
}

/** The short label of a picture option: the text before the first « : » (« Bild a: Kaffee » → « Bild a »). */
export function optionPictureLabel(option: string): string {
  const index = option.indexOf(":");
  return (index >= 0 ? option.slice(0, index) : option).trim();
}

/** Goethe A2 Hören Teil 3: « Bild a: Kaffee » → « a » (the letter shown under the drawing). */
export function optionPictureLetter(option: string): string {
  const label = optionPictureLabel(option);
  const match = /(?:^|\s)([a-zA-Z0-9])$/.exec(label);
  return match ? (match[1] ?? label) : label;
}
